import backgroundImage from '../../assets/Form/FormBackground.jpg'
import logo from '../../assets/Form/DesktopLogo.png'
import LawyerImage from '../../assets/Form/LawyerImage.png'
import { useScroll } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { MoveLeft } from 'lucide-react'
import loader from '../../assets/Form/loader.svg'
import EligibleTick from '../../assets/Form/tick.svg'
import inEligible from '../../assets/Form/NotEligible.svg'
import thankYou from '../../assets/Form/ThankYou.svg'
import { MotorVehicleSendAdminEmail, MotorVehicleSendUserEmail } from '../../emailService'
import Disclaimer from '../Disclaimer'
import PrivacyPolicy from '../PrivacyPolicy'
import { buildAdvancedMatching, ensureMetaPixel } from '../../utils/metaPixel'
import { LeadVertical } from '../../utils/common'


const question = [
    {
        id: 1,
        question: 'Were you involved in a Motor Vehicle Accident?',
        type: 'radio',
        options: [
            'Yes',
            'No'
        ]
    },
    {
        id: 2,
        question: 'Were you injured and hospitalized?',
        type: 'radio',
        options: [
            'Yes',
            'No'
        ]
    },
    {
        id: 3,
        question: 'When did the accident happen?',
        type: 'radio',
        options: [
            '1 - 2 Weeks',
            '2 - 3 Months',
            '3 - 6 Months',
            '6 - 12 Months'
        ]
    },
    {
        id: 4,
        question: 'Do you currently have an accident claim for the same?',
        type: 'radio',
        options: [
            'Yes',
            'No'
        ]
    },
    {
        id: 5,
        question: 'Were you legally at fault?',
        type: 'radio',
        options: [
            'Yes',
            'No'
        ]
    },
    {
        id: 6,
        question: 'Do you already have an attorney?',
        type: 'radio',
        options: [
            'Yes',
            'No'
        ]
    },
    {
        id: 7,
        heading: 'Please provide your details so we can get to work',
        type: 'contact',
        fields: [
            'First Name',
            'Last Name',
            'Email Address',
            'Phone Number'
        ]
    }
]

const MotorVehicleAccidentForm = () => {
    const META_PIXEL_ID = "1424919209356817";
    let initialLandingUrl = null;

    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({
        FirstName: "",
        LastName: "",
        email: "",
        phoneNumber: "",
        legalAgree: false,
        verifyPerson: false,
    });
    const [errors, setErrors] = useState({});
    const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const checkValidations = useRef(false);

    
    const validate = ({ formData }) => {
        const newErrors = {};
        if (!formData.FirstName?.trim()) newErrors.FirstName = "Required";
        else if (!/^[a-zA-Z\s'-]+$/.test(formData.FirstName.trim()))
            newErrors.FirstName = "First name should only contain letters";

        if (!formData.LastName?.trim()) newErrors.LastName = "Required";
        else if (!/^[a-zA-Z\s'-]+$/.test(formData.LastName.trim()))
            newErrors.LastName = "Last name should only contain letters";

        if (!formData.email?.trim()) newErrors.email = "Required";
        else if (!/\S+@\S+\.\S+/.test(formData.email))
            newErrors.email = "Invalid email";

        if (!formData.phoneNumber?.trim()) newErrors.phoneNumber = "Required";
        else if (!/^\d{10}$/.test(formData.phoneNumber))
            newErrors.phoneNumber = "Phone number must be exactly 10 digits";

        if (!formData.legalAgree) newErrors.legalAgree = "Required";


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const HandleInputChange = ({ question, e }) => {
        let value = e.target.value;

        // For FirstName and LastName: only allow letters, spaces, hyphens, and apostrophes
        if (question === "FirstName" || question === "LastName") {
            value = value.replace(/[^a-zA-Z\s'-]/g, '');
        }

        // For phoneNumber: only allow digits and limit to 10 digits
        if (question === "phoneNumber") {
            value = value.replace(/\D/g, '').slice(0, 10);
        }

        setFormData({ ...formData, [question]: value })
        if (checkValidations.current) {
            validate({ formData: { ...formData, [question]: value } });
        }

        if (currentStep < 7) {
            setTimeout(() => {
                setCurrentStep(currentStep + 1);
            }, 400)
        }
    }

    const HandleCheckbox = ({ question, e }) => {
        setFormData({ ...formData, [question]: e.target.checked });
        if (checkValidations.current) {
            validate({ formData: { ...formData, [question]: e.target.checked } })
        }
    };

    const checkEligibility = () => {
        // Check if user was involved in MVA
        if (formData[question[0].question] === 'No') {
            return false;
        }
        // Check if user was injured and hospitalized
        if (formData[question[1].question] === 'No') {
            return false;
        }
        // Check if user already has an attorney
        if (formData[question[5].question] === 'Yes') {
            return false;
        }
        // Check if user was legally at fault
        if (formData[question[4].question] === 'Yes') {
            return false;
        }
        // Check if user already has a claim
        if (formData[question[3].question] === 'Yes') {
            return false;
        }
        return true;
    }
    const getSourceUrl = () => {
        if (typeof window === "undefined") return "Unknown";

        // If we haven't stored the initial URL yet, store it
        if (!initialLandingUrl) {
            initialLandingUrl = window.location.href;
        }

        return initialLandingUrl;
    };
    const getIPAddress = async () => {
        try {
            const response = await fetch("https://api.ipify.org?format=json");
            const data = await response.json();
            return data.ip;
        } catch (error) {
            console.error("Failed to get IP address:", error);
            return "IP address not available";
        }
    };
    const saveToCRM = async ({ payload }) => {
        try {
            const ipAddress = await getIPAddress();
            const response = await fetch('https://crm-internal-backend-ayb9fqawg8b6bjen.canadacentral-01.azurewebsites.net/api/submitformdata', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    countryName: 'USA',
                    brandName: 'Project 6',
                    websiteName: 'ConnectWithLawyers',
                    formname: 'Motor Vehicle accident 001',
                    vertical: LeadVertical.MOTOR_VEHICLE_ACCIDENTS,
                    data: {
                        submissionDate: new Date().toLocaleString(),
                        FirstName: payload.FirstName,
                        LastName: payload.LastName,
                        email: payload.email,
                        phoneNumber: payload.phoneNumber,
                        q1: payload['Were you involved in a Motor Vehicle Accident?'],
                        q2: payload['Were you injured and hospitalized?'],
                        q3: payload['When did the accident happen?'],
                        q4: payload['Do you currently have an accident claim for the same?'],
                        q5: payload['Were you legally at fault?'],
                        q6: payload['Do you already have an attorney?'],
                        trustedFormCertUrl: payload.certId,
                        trustedFormPingUrl: payload.pingUrl,
                        trustedFormToken: payload.tokenUrl,
                        ip_address: ipAddress,
                        page_source: getSourceUrl(),
                    }
                })
            });
            return true
        }
        catch (e) {
            console.log(e);
            return false;
        }
    }
    const HandleSubmit = async (e) => {
        e.preventDefault()
        if (!validate({ formData: formData })) {
            checkValidations.current = true;
            return;
        }

        setIsSubmitting(true);

        try {
            const getTrustedFormData = () => {
                return new Promise((resolve) => {
                    // Try multiple ways to get the data
                    const certUrl = document.querySelector("[name='xxTrustedFormCertUrl']")?.value ||
                        document.getElementById('xxTrustedFormCertUrl')?.value;

                    const tokenUrl = document.querySelector("[name='xxTrustedFormCertToken']")?.value ||
                        document.getElementById('xxTrustedFormCertToken')?.value;

                    const pingUrl = document.querySelector("[name='xxTrustedFormPingUrl']")?.value ||
                        document.getElementById('xxTrustedFormPingUrl')?.value;

                    // If we don't have the data immediately, wait a bit
                    if (!certUrl) {
                        setTimeout(() => {
                            const retryCertUrl = document.querySelector("[name='xxTrustedFormCertUrl']")?.value ||
                                document.getElementById('xxTrustedFormCertUrl')?.value;
                            const retryTokenUrl = document.querySelector("[name='xxTrustedFormCertToken']")?.value ||
                                document.getElementById('xxTrustedFormCertToken')?.value;
                            const retryPingUrl = document.querySelector("[name='xxTrustedFormPingUrl']")?.value ||
                                document.getElementById('xxTrustedFormPingUrl')?.value;

                            resolve({
                                certId: retryCertUrl,
                                tokenUrl: retryTokenUrl,
                                pingUrl: retryPingUrl
                            });
                        }, 500);
                    } else {
                        resolve({
                            certId: certUrl,
                            tokenUrl: tokenUrl,
                            pingUrl: pingUrl
                        });
                    }
                });
            };

            const trustedFormData = await getTrustedFormData();

            const payload = {
                ...formData,
                ...trustedFormData,
            };

            const sentToCrm = await saveToCRM({ payload })
            await MotorVehicleSendAdminEmail({ formData: payload });
            await MotorVehicleSendUserEmail({ formData: payload });
            if (sentToCrm) {
                setCurrentStep('thankyou')
                // Track Lead pixel when form is submitted (Thank You page)
                ensureMetaPixel(
                    META_PIXEL_ID,
                    buildAdvancedMatching({ email: payload.email, phoneNumber: payload.phoneNumber })
                );
                if (window.fbq) {
                    window.fbq('track', 'Lead');
                }
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setIsSubmitting(false);
        }

    }
    useEffect(() => {
        ensureMetaPixel(META_PIXEL_ID);
    }, []);

    

    useEffect(() => {
        if (currentStep == 7) {
            if (checkEligibility()) { setTimeout(() => { setCurrentStep('eligible') }, 2000) }
            else setTimeout(() => { setCurrentStep('Not Eligible') }, 2000)
        }
        if (currentStep === 'eligible') {
            setTimeout(() => { setCurrentStep('contactForm') }, 2000)
        }
    }, [currentStep]);

    return (
        <div
            className="flex flex-col items-center justify-start pt-4 sm:pt-[10vh] px-4 sm:px-0 bg-cover bg-center h-screen gap-4 sm:gap-[10vh] overflow-hidden"
            style={{ backgroundImage: `url(${backgroundImage})` }}
        >
            {/* className="w-full max-w-lg mb-2 sm:mb-0" */}
            <div> 
                <img src={logo} className=" w-full" />
            </div>

            {currentStep == 0 && <div
                className="
      bg-white flex flex-col items-center p-6 sm:p-8 rounded-lg
      shadow-[0_4px_8px_rgba(0,0,0,0.20)]
      w-full max-w-lg max-h-[80vh] overflow-scroll scrollbar-hide mt-4 sm:mt-2
    "
            >
                <img src={LawyerImage} className="max-w-full w-40 sm:w-40 md:w-60 lg-w-full xxl:w-full" />

                <div className="text-[#162766] font-lato font-semibold text-2xl lg:text-[32px] text-center mt-4">
                    Were you injured in a motor<br />vehicle accident?
                </div>

                <div className="text-[#162766] font-lato font-semibold text-lg sm:text-[18px] text-center mt-2">
                    You may be entitled to compensation.
                </div>

                <div
                    className="
        text-sm sm:text-[16px] text-[#162766] w-full rounded-xl
        bg-[rgba(22,39,102,0.06)] text-center py-2 mt-2
      "
                >
                    Take a few minutes to answer a few quick<br />questions to see if you have a case.
                </div>

                <button onClick={() => {
                    // Track PageView and CompleteRegistration when Continue is clicked
                    if (window.fbq) {
                        window.fbq('track', 'PageView');
                        window.fbq('track', 'CompleteRegistration');
                    }
                    setCurrentStep(currentStep + 1);
                }}
                    className="
        text-center text-[#162766] text-lg sm:text-[20px] w-full
        shadow-[0_5px_24px_0_rgba(0,0,0,0.25)]
        bg-[#F8D216] rounded-lg py-2 mt-4
      "
                >
                    Continue
                </button>
            </div>}
            {currentStep > 0 && currentStep < 7 && (
                <div className="w-full max-w-lg mt-4 sm:mt-0">
                    {/* Back */}
                    <div class="relative w-full flex items-center text-[#162766] text-xl gap-4 mb-2">
                        <MoveLeft
                            class="cursor-pointer absolute left-0"
                            onClick={() => setCurrentStep(currentStep - 1)}
                        />
                        <div class="mx-auto text-[rgba(66,66,66,0.90)] font-inter text-[16px] font-semibold leading-[34px] uppercase">
                            Motor Vehicle Accident Free Case Review
                        </div>
                    </div>

                    <div
                        className="
      bg-white flex flex-col items-center p-6 sm:p-8 rounded-lg
      shadow-[0_4px_8px_rgba(0,0,0,0.20)]
      w-full max-w-lg max-h-[80vh] overflow-scroll scrollbar-hide
    "
                    >
                        {/* Top loading bar */}
                        <div className="w-full relative mb-4">
                            <div className="h-[6px] w-full bg-[#E5E7EB] rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-[#F8D216] transition-all duration-300"
                                    style={{ width: `${((currentStep) / 6) * 100}%` }}
                                />
                            </div>
                        </div>



                        {/* Title */}
                        <div className="text-[#162766] font-lato font-semibold text-2xl lg:text-[28px] text-center">
                            {question[currentStep - 1].question}
                        </div>

                        {/* Options */}
                        <div className="flex flex-col gap-4 mt-6 w-full">
                            {question[currentStep - 1].options.map((opt, idx) => (
                                <label
                                    key={idx}
                                    className="
            flex items-center gap-3 bg-[#F8F9FA]
            rounded-full py-3 px-4 cursor-pointer
          "
                                >
                                    <input
                                        id={`q-${currentStep - 1}-check-${idx}`}
                                        type="radio"
                                        name={`q-${currentStep - 1}`}
                                        checked={formData[question[currentStep - 1].question] === opt}
                                        className="h-4 w-4"
                                        value={opt}
                                        onClick={(e) => { HandleInputChange({ question: question[currentStep - 1].question, e }) }}
                                    // checked={formData[question[currentStep - 1]] == opt}
                                    />
                                    <span className="text-[#162766] text-sm sm:text-base">
                                        {opt}
                                    </span>
                                </label>
                            ))}
                        </div>


                    </div></div>

            )}
            {currentStep == 7 && (
                <div className="flex justify-center mt-12 px-4 sm:px-0">
                    <div
                        className="
        bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.15)]
        px-6 py-20
        w-full
        max-w-[480px] sm:max-w-[560px] lg:max-w-[640px]
        text-center
        relative
      "
                    >
                        {/* Top Gold Accent Bar */}
                        <div className="absolute top-0 left-0 w-full h-[9px] bg-[#F8D216] rounded-t-xl"></div>

                        {/* Circular Loader */}
                        <div className="flex justify-center mb-4">
                            <div className="relative w-28 h-28">
                                <div className="absolute inset-0 border-4 border-[#E6E6E6] rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-[#023437] border-t-transparent rounded-full animate-spin"></div>
                                <img src={loader} alt="" className="absolute inset-0 m-auto w-14 h-14" />
                            </div>
                        </div>

                        {/* Text */}
                        <div className="text-[#023437] font-medium text-3xl break-words">
                            Checking your eligibility. This should take less than a minute.

                        </div>

                    </div>
                </div>
            )}
            {currentStep == "eligible" && (
                <div className="w-full flex justify-center mt-6 px-4">
                    <div className="
      bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.15)]
      px-6 sm:px-10 py-16
      w-full max-w-[650px]
      text-center
      relative
      animate-fade-in
    ">
                        <div className="absolute top-0 left-0 w-full h-[10px] bg-[#F8D216] rounded-t-2xl" />

                        <div className="flex justify-center w-full">
                            <img
                                src={EligibleTick}
                                className="w-40 sm:w-52 animate-pop"
                            />
                        </div>

                        <h2 className="text-[#162766] font-semibold text-[28px] mb-3 mt-3">
                            You’re Eligible!
                        </h2>

                        <p className="text-[#162766] text-[18px] leading-relaxed max-w-[420px] mx-auto">
                            You meet the criteria. You’re one step closer
                            to getting the help you deserve.
                        </p>
                    </div>
                </div>
            )}

            {currentStep == 'Not Eligible' && (
                <div className="w-full flex flex-col justify-center items-center mt-6 px-4">

                    <div className="w-full max-w-[650px] flex items-center gap-3 mb-4">
                        <MoveLeft
                            className="text-[#162766] text-xl cursor-pointer"
                            onClick={() => setCurrentStep(1)}
                        />

                        <span className="text-[#162766] text-sm font-medium tracking-wide">
                            MOTOR VEHICLE ACCIDENT FREE CASE REVIEW
                        </span>
                    </div>

                    <div
                        className="
        bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.15)]
        px-6 sm:px-10 py-16
        w-full max-w-[650px]
        text-center
        relative
      "
                    >
                        <div className="absolute top-0 left-0 w-full h-[10px] bg-[#F8D216] rounded-t-2xl" />

                        {/* updated image */}
                        <div className="flex justify-center w-full">
                            <img
                                src={inEligible}
                                className="w-40 sm:w-52 animate-pop"
                            />
                        </div>

                        <h2 className="text-[#162766] font-semibold text-[28px] mb-3 mt-2">
                            You’re Not Eligible!
                        </h2>

                        <p className="text-[#162766] text-[15px] leading-relaxed max-w-[420px] mx-auto">
                            You’re not eligible for this claim, but you may
                            qualify for other assistance options.
                        </p>
                    </div>
                </div>
            )}



            {currentStep === "contactForm" && (
                <form className="w-full flex justify-center mt-6 px-4">
                    <input
                        type="hidden"
                        id="xxTrustedFormCertUrl"
                        name="xxTrustedFormCertUrl"
                    //   value={certId}
                    />
                    <input
                        type="hidden"
                        id="xxTrustedFormCertToken"
                        name="xxTrustedFormCertToken"
                    //   value={tokenUrl}
                    />
                    <input
                        type="hidden"
                        id="xxTrustedFormPingUrl"
                        name="xxTrustedFormPingUrl"
                    //   value={pingUrl}
                    />
                    <div
                        className="
        bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.15)]
        px-6 sm:px-10 py-6 sm:py-10
        w-full max-w-[650px]
        text-center
        relative
        sm:max-h-[75vh] sm:overflow-auto
        scrollbar-hide
        text-base sm:text-lg
      "
                    >
                        <div className="absolute top-0 left-0 w-full h-[6px] bg-[#F8D216] rounded-t-2xl" />

                        <h2 className="text-[#162766] font-semibold text-2xl sm:text-3xl leading-snug mb-8">
                            Please provide your details<br />so we can get to work
                        </h2>

                        <div className="flex flex-col gap-4 text-left">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                    <input
                                        type="text"
                                        placeholder="First Name"
                                        value={formData.FirstName}
                                        onChange={(e) => HandleInputChange({ question: "FirstName", e })}
                                        className={`
                px-4 py-3 w-full rounded-lg focus:outline-none
                border
                text-base sm:text-lg
                ${errors.FirstName ? "border-red-500 text-red-500" : "border-[#CBD4E1]"}
              `}
                                    />
                                    {errors.FirstName && (
                                        <span className="text-[12px] text-red-500 mt-1">{errors.FirstName}</span>
                                    )}
                                </div>

                                <div className="flex flex-col">
                                    <input
                                        type="text"
                                        placeholder="Last Name"
                                        value={formData.LastName}
                                        onChange={(e) => HandleInputChange({ question: "LastName", e })}
                                        className={`
                px-4 py-3 w-full rounded-lg focus:outline-none
                border
                text-base sm:text-lg
                ${errors.LastName ? "border-red-500 text-red-500" : "border-[#CBD4E1]"}
              `}
                                    />
                                    {errors.LastName && (
                                        <span className="text-[12px] text-red-500 mt-1">{errors.LastName}</span>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col">
                                <input
                                    type="email"
                                    placeholder="Email Address"
                                    value={formData.email}
                                    onChange={(e) => HandleInputChange({ question: "email", e })}
                                    className={`
              px-4 py-3 w-full rounded-lg focus:outline-none
              border
              text-base sm:text-lg
              ${errors.email ? "border-red-500 text-red-500" : "border-[#CBD4E1]"}
            `}
                                />
                                {errors.email && (
                                    <span className="text-[12px] text-red-500 mt-1">{errors.email}</span>
                                )}
                            </div>

                            <div className="flex flex-col">
                                <input
                                    type="text"
                                    placeholder="Phone Number"
                                    value={formData.phoneNumber}
                                    onChange={(e) => HandleInputChange({ question: "phoneNumber", e })}
                                    className={`
              px-4 py-3 w-full rounded-lg focus:outline-none
              border
              text-base sm:text-lg
              ${errors.phoneNumber ? "border-red-500 text-red-500" : "border-[#CBD4E1]"}
            `}
                                />
                                {errors.phoneNumber && (
                                    <span className="text-[12px] text-red-500 mt-1">{errors.phoneNumber}</span>
                                )}
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="flex items-start gap-3 mt-1 text-[#162766] leading-snug text-[12px] sm:text-base">
                                    <input
                                        type="checkbox"
                                        className="mt-1"
                                        checked={formData.legalAgree}
                                        onChange={(e) => HandleCheckbox({ question: "legalAgree", e })}
                                    />
                                    <span>
                                        I agree to the <span className="underline" onClick={() => setShowPrivacyPolicy(true)}
                                        >privacy policy</span> & <span className="underline" onClick={() => setShowDisclaimer(true)}
                                        >disclaimer</span> & give my express written consent, affiliates and/or lawyer to contact you at the number provided above, even if this number is a wireless number or if I am presently listed on a Do Not Call list. I understand that I may be contacted by telephone, email, text message or mail regarding case options and that I may be called using automatic dialing equipment. Message and data rates may apply. My consent does not require purchase. This is Legal advertising.
                                    </span>
                                </label>
                                {errors.legalAgree && (
                                    <span className="text-[12px] text-red-500 pl-7 -mt-1">
                                        {errors.legalAgree}
                                    </span>
                                )}
                            </div>
                        </div>

                        <button
                            onClick={HandleSubmit}
                            disabled={isSubmitting}
                            className={`
          w-full rounded-lg py-3 mt-8 font-medium
          shadow-[0_5px_24px_rgba(0,0,0,0.25)]
          text-lg sm:text-xl
          bg-[#F8D216] text-[#162766]
          flex items-center justify-center gap-2
          disabled:opacity-70 disabled:cursor-not-allowed
        `}
                        >
                            {isSubmitting && (
                                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            )}
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                </form>
            )}

            {/* Privacy Policy Modal */}
            {showPrivacyPolicy && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-[#162766]">Privacy Policy</h3>
                            <button
                                onClick={() => setShowPrivacyPolicy(false)}
                                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[70vh]">
                            <PrivacyPolicy />
                        </div>
                        <div className="flex justify-end p-6 border-t border-gray-200">
                            <button
                                onClick={() => setShowPrivacyPolicy(false)}
                                className="px-6 py-2 bg-[#F8D216] text-[#162766] rounded-lg font-medium hover:bg-[#e6c214]"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Disclaimer Modal */}
            {showDisclaimer && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-[#162766]">Disclaimer</h3>
                            <button
                                onClick={() => setShowDisclaimer(false)}
                                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[70vh]">
                            <Disclaimer />
                        </div>
                        <div className="flex justify-end p-6 border-t border-gray-200">
                            <button
                                onClick={() => setShowDisclaimer(false)}
                                className="px-6 py-2 bg-[#F8D216] text-[#162766] rounded-lg font-medium hover:bg-[#e6c214]"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {
                currentStep === "thankyou" && (
                    <
                        >
                        <img
                            src={thankYou}
                            className="w-120 mx-auto animate-pop"
                        />
                    </>
                )
            }





        </div >

    )
}

export default MotorVehicleAccidentForm;
